/**
 * VendorFooter - Beautiful organized footer for vendor pages
 * 
 * Design: Pizza Hut style organization with vendorOrders pastel theme
 * Visibility: Desktop/Tablet only (mobile uses bottom navigation)
 * Purpose: Easy access to all vendor features, organized by category
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useVendorConfig } from '../context/VendorConfigContext';
import {
  // Menu & Items
  DocumentTextIcon,
  QueueListIcon,
  // Kitchen & Operations
  FireIcon,
  TvIcon,
  ComputerDesktopIcon,
  // Venue
  TableCellsIcon,
  CreditCardIcon,
  // Business
  Cog8ToothIcon,
  ReceiptPercentIcon,
  GiftIcon,
  ChartBarIcon,
  // Staff
  UserGroupIcon,
  UsersIcon,
  // Support
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

// Category configuration with pastel colors
const categories = [
  {
    title: 'Menu & Items',
    color: 'from-orange-400 to-amber-500',
    bgLight: 'bg-orange-50',
    textColor: 'text-orange-600',
    borderColor: 'border-orange-200',
    icon: DocumentTextIcon,
    links: [
      { name: 'Menu Management', path: '/admin/vendors/:vendorId/menu', icon: DocumentTextIcon },
      { name: 'Multi-Menu Setup', path: '/vendor/multi-menu', icon: QueueListIcon, feature: 'multipleMenus' },
    ]
  },
  {
    title: 'Kitchen & Operations',
    color: 'from-purple-400 to-violet-500',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200',
    icon: FireIcon,
    links: [
      { name: 'Kitchen Display', path: '/vendor/kitchen', icon: ComputerDesktopIcon, feature: 'kitchenDisplay' },
      { name: 'Order Status Display', path: '/vendor/order-status-display', icon: TvIcon },
    ]
  },
  {
    title: 'Venue & Tables',
    color: 'from-cyan-400 to-teal-500',
    bgLight: 'bg-cyan-50',
    textColor: 'text-cyan-600',
    borderColor: 'border-cyan-200',
    icon: TableCellsIcon,
    links: [
      { name: 'Table Layout', path: '/vendor/tables', icon: TableCellsIcon, feature: 'tableManagement' },
      { name: 'Table Orders', path: '/vendor/tables/orders', icon: QueueListIcon, feature: 'tableManagement' },
    ]
  },
  {
    title: 'Business',
    color: 'from-blue-400 to-indigo-500',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    icon: ChartBarIcon,
    links: [
      { name: 'Finance', path: '/vendor/finance', icon: ChartBarIcon },
      { name: 'Configuration', path: '/vendor/configuration', icon: Cog8ToothIcon },
      { name: 'Tax Settings', path: '/vendor/tax-config', icon: ReceiptPercentIcon },
      { name: 'Loyalty Programs', path: '/vendor/loyalty', icon: GiftIcon, feature: 'loyaltyPrograms' },
      { name: 'Tips Distribution', path: '/vendor/tips', icon: GiftIcon, feature: 'tipsDistribution' },
      { name: 'Reports & Analytics', path: '/vendor/reports', icon: ChartBarIcon },
    ]
  },
  {
    title: 'Staff',
    color: 'from-emerald-400 to-green-500',
    bgLight: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    icon: UserGroupIcon,
    links: [
      { name: 'Steward Management', path: '/admin/stewards', icon: UsersIcon, feature: 'stewardFeatures' },
      { name: 'Steward Settings', path: '/vendor/steward-settings', icon: UserGroupIcon, feature: 'stewardFeatures' },
    ]
  },
  {
    title: 'Support',
    color: 'from-slate-400 to-gray-500',
    bgLight: 'bg-slate-50',
    textColor: 'text-slate-600',
    borderColor: 'border-slate-200',
    icon: WrenchScrewdriverIcon,
    links: [
      { name: 'Diagnostics', path: '/vendor/socket-diagnostics', icon: WrenchScrewdriverIcon },
    ]
  },
];

const VendorFooter: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { features } = useVendorConfig();
  const location = useLocation();

  // Only show for vendors
  if (!user?.roles?.includes('ROLE_VENDOR') && !user?.roles?.includes('ROLE_ADMIN')) {
    return null;
  }

  // Replace :vendorId with actual user id in paths
  const processPath = (path: string) => {
    return path.replace(':vendorId', String(user?.id || ''));
  };

  // Check if feature is enabled
  const isFeatureEnabled = (feature?: string) => {
    if (!feature) return true;
    return features?.[feature as keyof typeof features] ?? true;
  };

  // Filter links based on enabled features
  const getVisibleLinks = (links: typeof categories[0]['links']) => {
    return links.filter(link => isFeatureEnabled(link.feature));
  };

  // Check if current path matches link
  const isActive = (path: string) => {
    const processedPath = processPath(path);
    return location.pathname === processedPath || location.pathname.startsWith(processedPath);
  };

  return (
    <footer 
      className="hidden lg:block w-full mt-auto border-t"
      style={{ 
        backgroundColor: 'var(--bg-surface)',
        borderColor: theme.gray?.[200] || '#E5E7EB'
      }}
    >
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Section Title */}
        <div className="text-center mb-10">
          <h2 
            className="text-2xl font-bold mb-2"
            style={{ color: theme.text }}
          >
            Quick Access
          </h2>
          <p 
            className="text-sm"
            style={{ color: theme.gray?.[500] || '#6B7280' }}
          >
            All your restaurant management tools in one place
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {categories.map((category) => {
            const visibleLinks = getVisibleLinks(category.links);
            if (visibleLinks.length === 0) return null;

            const CategoryIcon = category.icon;

            return (
              <div key={category.title} className="space-y-4">
                {/* Category Header */}
                <div className="flex items-center gap-2">
                  <div 
                    className={`w-8 h-8 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center shadow-sm`}
                  >
                    <CategoryIcon className="w-4 h-4 text-white" />
                  </div>
                  <h3 
                    className="font-semibold text-sm"
                    style={{ color: theme.text }}
                  >
                    {category.title}
                  </h3>
                </div>

                {/* Category Links */}
                <ul className="space-y-2">
                  {visibleLinks.map((link) => {
                    const LinkIcon = link.icon;
                    const active = isActive(link.path);

                    return (
                      <li key={link.name}>
                        <Link
                          to={processPath(link.path)}
                          className={`
                            flex items-center gap-2 text-sm py-1.5 px-2 rounded-lg
                            transition-all duration-200 group
                            ${active 
                              ? `${category.bgLight} ${category.textColor} font-medium` 
                              : 'hover:bg-gray-50'
                            }
                          `}
                          style={{ 
                            color: active ? undefined : (theme.gray?.[600] || '#4B5563')
                          }}
                        >
                          <LinkIcon 
                            className={`w-4 h-4 transition-transform group-hover:scale-110 ${
                              active ? category.textColor : 'text-gray-400'
                            }`} 
                          />
                          <span className="truncate">{link.name}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Bar */}
      <div 
        className="border-t py-6 px-6"
        style={{ 
          backgroundColor: theme.gray?.[50] || '#F9FAFB',
          borderColor: theme.gray?.[200] || '#E5E7EB'
        }}
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Left - Branding */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <div>
              <p 
                className="font-semibold text-sm"
                style={{ color: theme.text }}
              >
                Scan Order & Go
              </p>
              <p 
                className="text-xs"
                style={{ color: theme.gray?.[500] || '#6B7280' }}
              >
                Restaurant Management Platform
              </p>
            </div>
          </div>

          {/* Center - Quick Stats Hint */}
          <div className="flex items-center gap-6 text-sm">
            <Link 
              to="/vendor/orders?status=pending"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              style={{ color: 'var(--status-warn)' }}
            >
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              <span>Pending</span>
            </Link>
            <Link 
              to="/vendor/kitchen"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              style={{ color: 'var(--accent-alt)' }}
            >
              <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse"></span>
              <span>Kitchen</span>
            </Link>
          </div>

          {/* Right - Copyright */}
          <p 
            className="text-xs"
            style={{ color: theme.gray?.[400] || '#9CA3AF' }}
          >
            © {new Date().getFullYear()} SOG. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default VendorFooter;



